import { Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { ExportLocation, ShApiCachingHelper, ShPaginationService } from '@stylehaven/components';
import { HttpService } from '../http/http.service';

@Injectable({ providedIn: 'root' })
export class UserExportLocationsService {
    private readonly exportLocationsEndpoint: string = `v2/export-locations`;
    private readonly userExportLocationsEndpoint: string = `v2/users/current/export-locations`;
    private exportLocations$: Observable<ExportLocation[]>;
    private defaultExportLocation$: Observable<ExportLocation>;

    constructor(
        private readonly http: HttpService,
        private readonly paginationService: ShPaginationService,
        private apiCachingHelper: ShApiCachingHelper
    ) {
        this.initialize();
    }

    initialize() {
        this.exportLocations$ = this.apiCachingHelper
            .cachedApiCall(this.paginationService.getAll<ExportLocation>(`${this.userExportLocationsEndpoint}`))
            .pipe(
                catchError(error => {
                    console.error('ExportLocationsService:: Failed to retrieve export locations', error);
                    return of([]);
                })
            );
    }

    get downloadableLocations$(): Observable<ExportLocation[]> {
        return this.getExportLocations().pipe(
            map((exportLocations: ExportLocation[]) =>
                exportLocations.filter((exportLocation: ExportLocation) => exportLocation.HttpAccessible)
            )
        );
    }

    get externalLocations$(): Observable<ExportLocation[]> {
        return this.getExportLocations().pipe(
            map((exportLocations: ExportLocation[]) =>
                exportLocations.filter((exportLocation: ExportLocation) => !exportLocation.HttpAccessible)
            )
        );
    }

    public getExportLocations(): Observable<ExportLocation[]> {
        return this.exportLocations$.pipe(take(1));
    }

    private initDefaultExportLocation(): Observable<ExportLocation> {
        const request = `${this.exportLocationsEndpoint}/default`;

        const defaultCall$: Observable<ExportLocation> = this.http.get<ExportLocation>(request).pipe(
            catchError((error: any) => {
                console.error(
                    'ExportLocationsService: no default export location found. Attempting to return the first export location possible.'
                );
                try {
                    return this.getExportLocations().pipe(
                        map(
                            (allExportLocations: ExportLocation[]) =>
                                allExportLocations.filter(exportLocation => exportLocation.HttpAccessible)[0]
                        )
                    );
                } catch {
                    console.error(
                        'ExportLocationsService: no export locations found. None at all. Zilch. Zero. Nothing.'
                    );
                    return throwError(error);
                }
            })
        );

        const cachedDefaultExportLocation$: Observable<any> = this.apiCachingHelper.cachedApiCall(defaultCall$);
        cachedDefaultExportLocation$.subscribe(
            (defaultExportLocation: ExportLocation) => (this.defaultExportLocation$ = of(defaultExportLocation))
        );

        return cachedDefaultExportLocation$.pipe(take(1));
    }

    public getDefaultExportLocation(): Observable<ExportLocation> {
        return this.defaultExportLocation$ ? this.defaultExportLocation$ : this.initDefaultExportLocation();
    }
}
